import { ensureNotCancelled, isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { InputValidationResultKind, InputValidationResult } from "./input";
import { IValidationRule, IValidationContext } from "./rules";
import { RuleValidationAction, RuleValidationTrigger } from "./rules/triggers";
import { ValidationRuleState } from "./state";
import { ExecuteValidationParams } from "./validationRunner";
import { ModelPath } from "./path";

export type RuleTriggerParams = {
	action: Exclude<RuleValidationAction, "formSubmit" | "stepSubmit">;
	matchesOwnPath: boolean;
} | {
	action: "stepSubmit";
	doesMatchStepPath: boolean;
} | {
	action: "formSubmit";
};

export async function executeRule<TValue>(
	value: TValue,
	validationContext: IValidationContext,
	rule: IValidationRule<TValue>,
	params: RuleTriggerParams,
	previousState: ValidationRuleState | undefined,
	signal?: AbortSignal
): Promise<ValidationRuleState> {
	ensureNotCancelled(signal);

	let isActivated: boolean = previousState?.isActivated ?? false;
	if (!isActivated) {
		if (isTriggered(rule.activationTrigger, params)) {
			isActivated = true;
		}
	}

	if (!isActivated) {
		return {
			isActivated: false,
			result: {
				kind: InputValidationResultKind.Valid
			}
		};
	}

	const previousResult: InputValidationResult = previousState?.result ?? {
		kind: InputValidationResultKind.Valid
	};

	const ruleTrigger: RuleValidationTrigger = previousResult.kind === InputValidationResultKind.Invalid
		? rule.fixErrorTrigger
		: rule.validationTrigger;

	if (!isTriggered(ruleTrigger, params)) {
		return {
			isActivated: true,
			result: previousResult
		};
	}

	try {
		const result: InputValidationResult = await Promise.resolve(rule.validate(value, validationContext));

		ensureNotCancelled(signal);

		return {
			isActivated: true,
			result
		};

	} catch (e) {
		if (isCancelledError(e)) {
			throw e;
		}

		return {
			isActivated: true,
			result: {
				kind: InputValidationResultKind.Invalid,
				errorMessage: localize("_UnknownError")
			}
		};
	}
}

export function convertToRuleTriggerParams(
	nodeModelPath: ModelPath,
	params: ExecuteValidationParams
): RuleTriggerParams {
	if (params.action === "formSubmit") {
		return params;
	}

	if (params.action === "stepSubmit") {
		let doesMatchStepPath: boolean = false;
		for (const initiatorModelPath of params.initiatorModelPaths) {
			if (nodeModelPath.equals(initiatorModelPath)
				|| nodeModelPath.isDescendentOf(initiatorModelPath)
			) {
				doesMatchStepPath = true;
				break;
			}
		}
		return {
			action: params.action,
			doesMatchStepPath
		};
	}

	return {
		action: params.action,
		matchesOwnPath: nodeModelPath.equals(params.initiatorModelPath)
	};
}

function isTriggered(
	trigger: RuleValidationTrigger,
	params: RuleTriggerParams
): boolean {
	switch (params.action) {
		case "formSubmit":
			return true;
		case "stepSubmit":
			return params.doesMatchStepPath;
		case "inputFinished":
			if (params.matchesOwnPath) {
				return isInCollection(trigger, "anyInputFinished", "anyInput", "inputFinished", "input");
			} else {
				return isInCollection(trigger, "anyInputFinished", "anyInput");
			}
		case "input":
			if (params.matchesOwnPath) {
				return isInCollection(trigger, "anyInput", "input");
			} else {
				return isInCollection(trigger, "anyInput");
			}
		default:
			throw never(params);
	}
}

function isInCollection(value: RuleValidationTrigger, ...collection: ReadonlyArray<RuleValidationTrigger>): boolean {
	for (const compare of collection) {
		if (value === compare) {
			return true;
		}
	}

	return false;
}